// src/components/EditTrailer.js

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import Select from "react-select";
import { useTranslation } from "react-i18next";

import {
  FaArrowLeft,
  FaBars,
  FaCalendarAlt,
  FaCheckCircle,
  FaDownload,
  FaEye,
  FaExclamationTriangle,
  FaFileAlt,
  FaFireExtinguisher,
  FaHashtag,
  FaIndustry,
  FaLayerGroup,
  FaLink,
  FaRoad,
  FaRulerCombined,
  FaRulerHorizontal,
  FaRulerVertical,
  FaSave,
  FaShieldAlt,
  FaUpload,
  FaWeightHanging,
} from "react-icons/fa";

import axios from "../axiosConfig";
import "../styles/EditTrailer.css";

/* =========================================================
   CONSTANTS
========================================================= */

const FILES_BASE =
  (typeof process !== "undefined" &&
    (
      process.env.REACT_APP_FILES_BASE_URL ||
      process.env.REACT_APP_API_BASE_URL ||
      process.env.REACT_APP_API_URL
    )) ||
  "https://api.loadevo.com";

const TRAILER_DETAILS_PREFIX =
  "trailer:detail:";

const TRAILER_DETAILS_TIMESTAMP_PREFIX =
  "trailer:detail:ts:";

const TRAILER_SUMMARY_CACHE_SUFFIX =
  ":trailers:summary:v2";

const TRAILER_SUMMARY_UPDATED_EVENT =
  "loadevo:trailer-summary-cache-updated";

const DOCUMENT_EXPIRY_WARNING_DAYS =
  60;

const DAY_MS =
  24 * 60 * 60 * 1000;

const EMPTY_TRAILER = {
  trailerPlate: "",
  brand: "",
  type: "",
  month: "",
  year: "",
  status: "ACTIVE",

  height: "",
  width: "",
  length: "",
  weight: "",

  numberOfAxles: "",
  numberOfStraps: "",
  numberOfRatchets: "",
  numberOfWoodenBeams: "",
  numberOfEdgeProtectors: "",
  numberOfAntiSlippingMattresses: "",
  numberOfSidebars: "",
  numberOfFireExtinguishers: "",

  inspection: null,
  inspectionExpiry: "",

  vehicleRegistrationCertificate: null,
  vehicleRegistrationCertificateExpiry: "",

  trailerInsurance: null,
  trailerInsuranceExpiry: "",
};

const TRAILER_TYPES = [
  "Tautliner",
  "Van",
  "Open Box Truck",
  "Refrigerated Truck",
  "Vehicle Carrier",
  "Container Truck",
  "Clothing Hanging Carrier",
  "Cattle Carrier",
  "Dump Carrier",
  "Cistern",
  "Special Transport Truck",
  "Battery Carrier",
  "Flatbed Truck",
  "Box Truck",
  "Lowboy Truck",
];

/* =========================================================
   GENERIC HELPERS
========================================================= */

function safeParse(raw) {
  if (!raw) {
    return null;
  }

  try {
    const parsed =
      JSON.parse(raw);

    if (
      parsed &&
      typeof parsed === "object" &&
      parsed.data &&
      typeof parsed.data === "object" &&
      !Array.isArray(parsed.data)
    ) {
      return parsed.data;
    }

    return parsed;
  } catch {
    return null;
  }
}

function formatDate(value) {
  if (!value) {
    return "";
  }

  return String(value)
    .split("T")[0];
}

function normalizeNumericValue(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "";
  }

  const parsed =
    Number(value);

  return Number.isFinite(parsed)
    ? parsed
    : value;
}

function normalizePlate(value) {
  return String(value || "")
    .trim()
    .toUpperCase();
}

function cleanFileName(
  value,
  fallback = "file"
) {
  if (!value) {
    return fallback;
  }

  let clean =
    String(value)
      .replace(/\\/g, "/")
      .split("#")[0]
      .split("?")[0];

  let name =
    clean.split("/").pop() ||
    fallback;

  try {
    name =
      decodeURIComponent(name);
  } catch {
    // Mantém o valor original.
  }

  return (
    name
      .replace(/[/\\?%*:|"<>]/g, "-")
      .trim() ||
    fallback
  );
}

function fileName(ref) {
  if (!ref) {
    return "";
  }

  if (
    typeof File !== "undefined" &&
    (
      ref instanceof File ||
      ref instanceof Blob
    )
  ) {
    return cleanFileName(
      ref.name || "file"
    );
  }

  if (
    typeof ref === "object"
  ) {
    return cleanFileName(
      ref.name ||
      ref.url ||
      "file"
    );
  }

  return cleanFileName(ref);
}

function normalizeDocumentReference(value) {
  if (!value) {
    return null;
  }

  if (
    typeof File !== "undefined" &&
    (
      value instanceof File ||
      value instanceof Blob
    )
  ) {
    return value;
  }

  if (
    typeof value === "object" &&
    value.url
  ) {
    return {
      ...value,

      name:
        value.name ||
        fileName(value.url),

      url:
        value.url,
    };
  }

  return {
    name:
      fileName(value),

    url:
      value,
  };
}

function buildFileUrl(ref) {
  if (!ref) {
    return "";
  }

  if (
    typeof File !== "undefined" &&
    (
      ref instanceof File ||
      ref instanceof Blob
    )
  ) {
    try {
      return URL.createObjectURL(ref);
    } catch {
      return "";
    }
  }

  const raw =
    typeof ref === "object"
      ? ref.url || ""
      : ref;

  const value =
    String(raw || "");

  if (!value) {
    return "";
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  const clean =
    (
      "/" +
      value
    )
      .replace(/\\/g, "/")
      .replace(/\/{2,}/g, "/");

  return `${FILES_BASE.replace(/\/+$/, "")}${clean}`;
}

function isPdfFile(ref) {
  if (!ref) {
    return false;
  }

  if (
    typeof File !== "undefined" &&
    ref instanceof File
  ) {
    return (
      ref.type === "application/pdf" ||
      /\.pdf$/i.test(ref.name || "")
    );
  }

  const raw =
    typeof ref === "object"
      ? String(
          ref.url ||
          ref.name ||
          ""
        )
      : String(ref || "");

  const clean =
    raw
      .toLowerCase()
      .split("#")[0]
      .split("?")[0];

  return clean.endsWith(".pdf");
}

function isImageFile(ref) {
  if (!ref) {
    return false;
  }

  if (
    typeof File !== "undefined" &&
    ref instanceof File
  ) {
    return (
      String(ref.type || "").startsWith("image/") ||
      /\.(jpg|jpeg|png|webp|gif|bmp|svg)$/i.test(
        ref.name || ""
      )
    );
  }

  const raw =
    typeof ref === "object"
      ? String(
          ref.url ||
          ref.name ||
          ""
        )
      : String(ref || "");

  const clean =
    raw
      .toLowerCase()
      .split("#")[0]
      .split("?")[0];

  return /\.(jpg|jpeg|png|webp|gif|bmp|svg)$/i.test(clean);
}

function normalizeIncomingTrailer(
  source = {}
) {
  const inspection =
    source.inspection ??
    source.Inspection ??
    null;

  const registrationCertificate =
    source.vehicleRegistrationCertificate ??
    source.VehicleRegistrationCertificate ??
    null;

  const insurance =
    source.trailerInsurance ??
    source.TrailerInsurance ??
    null;

  return {
    ...EMPTY_TRAILER,

    trailerPlate:
      source.trailerPlate ??
      source.trailerNumber ??
      "",

    brand:
      source.brand ??
      source.trailerBrand ??
      source.trailer_brand ??
      "",

    type:
      source.type ??
      source.trailerType ??
      "",

    month:
      normalizeNumericValue(
        source.month ??
        source.Month
      ),

    year:
      normalizeNumericValue(
        source.year ??
        source.Year
      ),

    status:
      source.status ??
      source.Status ??
      "ACTIVE",

    height:
      source.height ??
      "",

    width:
      source.width ??
      "",

    length:
      source.length ??
      "",

    weight:
      source.weight ??
      "",

    numberOfAxles:
      source.numberOfAxles ??
      "",

    numberOfStraps:
      source.numberOfStraps ??
      "",

    numberOfRatchets:
      source.numberOfRatchets ??
      "",

    numberOfWoodenBeams:
      source.numberOfWoodenBeams ??
      "",

    numberOfEdgeProtectors:
      source.numberOfEdgeProtectors ??
      "",

    numberOfAntiSlippingMattresses:
      source.numberOfAntiSlippingMattresses ??
      source.numberOfAntiSlipMats ??
      "",

    numberOfSidebars:
      source.numberOfSidebars ??
      "",

    numberOfFireExtinguishers:
      source.numberOfFireExtinguishers ??
      "",

    inspection:
      normalizeDocumentReference(
        inspection
      ),

    inspectionExpiry:
      formatDate(
        source.inspectionExpiry ??
        source.InspectionExpiry
      ),

    vehicleRegistrationCertificate:
      normalizeDocumentReference(
        registrationCertificate
      ),

    vehicleRegistrationCertificateExpiry:
      formatDate(
        source.vehicleRegistrationCertificateExpiry ??
        source.VehicleRegistrationCertificateExpiry
      ),

    trailerInsurance:
      normalizeDocumentReference(
        insurance
      ),

    trailerInsuranceExpiry:
      formatDate(
        source.trailerInsuranceExpiry ??
        source.TrailerInsuranceExpiry
      ),
  };
}

function unwrapTrailerResponse(payload) {
  const candidates = [
    payload?.trailer,
    payload?.data?.trailer,
    payload?.data,
    payload,
  ];

  return (
    candidates.find((candidate) => {
      return (
        candidate &&
        typeof candidate === "object" &&
        !Array.isArray(candidate)
      );
    }) ||
    {}
  );
}

function readTrailerDetailsCache(
  trailerId
) {
  if (
    typeof window === "undefined" ||
    trailerId === null ||
    trailerId === undefined ||
    trailerId === ""
  ) {
    return null;
  }

  try {
    return safeParse(
      window.localStorage.getItem(
        `${TRAILER_DETAILS_PREFIX}${trailerId}`
      )
    );
  } catch {
    return null;
  }
}

function writeTrailerDetailsCache(
  trailerId,
  trailer
) {
  if (
    typeof window === "undefined" ||
    trailerId === null ||
    trailerId === undefined ||
    trailerId === "" ||
    !trailer ||
    typeof trailer !== "object"
  ) {
    return;
  }

  try {
    window.localStorage.setItem(
      `${TRAILER_DETAILS_PREFIX}${trailerId}`,
      JSON.stringify(trailer)
    );

    window.localStorage.setItem(
      `${TRAILER_DETAILS_TIMESTAMP_PREFIX}${trailerId}`,
      String(Date.now())
    );
  } catch {
    // Storage indisponível.
  }
}

function getSummaryCacheItems(parsed) {
  if (Array.isArray(parsed)) {
    return parsed;
  }

  if (
    parsed &&
    typeof parsed === "object" &&
    Array.isArray(parsed.items)
  ) {
    return parsed.items;
  }

  if (
    parsed &&
    typeof parsed === "object" &&
    parsed.data &&
    typeof parsed.data === "object" &&
    Array.isArray(parsed.data.items)
  ) {
    return parsed.data.items;
  }

  return [];
}

function writeSummaryCacheItems(
  cacheKey,
  parsed,
  items
) {
  if (
    typeof window === "undefined"
  ) {
    return;
  }

  const now =
    Date.now();

  let nextValue;

  if (Array.isArray(parsed)) {
    nextValue =
      items;
  } else if (
    parsed &&
    typeof parsed === "object" &&
    parsed.data &&
    typeof parsed.data === "object" &&
    Array.isArray(parsed.data.items)
  ) {
    nextValue = {
      ...parsed,

      savedAt:
        now,

      updatedAt:
        now,

      meta: {
        ...(parsed.meta || {}),
        etag: null,
      },

      data: {
        ...parsed.data,
        items,
      },
    };
  } else {
    nextValue = {
      ...(
        parsed &&
        typeof parsed === "object"
          ? parsed
          : {}
      ),

      items,

      savedAt:
        now,

      updatedAt:
        now,

      meta: {
        ...(parsed?.meta || {}),
        etag: null,
      },
    };
  }

  window.localStorage.setItem(
    cacheKey,
    JSON.stringify(nextValue)
  );
}

function patchTrailerSummaryCaches(
  trailer
) {
  if (
    typeof window === "undefined" ||
    !trailer ||
    typeof trailer !== "object" ||
    trailer.id === null ||
    trailer.id === undefined
  ) {
    return;
  }

  const trailerId =
    String(trailer.id);

  try {
    const matchingKeys = [];

    for (
      let index = 0;
      index < window.localStorage.length;
      index += 1
    ) {
      const key =
        window.localStorage.key(index);

      if (
        key &&
        key.endsWith(
          TRAILER_SUMMARY_CACHE_SUFFIX
        )
      ) {
        matchingKeys.push(key);
      }
    }

    matchingKeys.forEach((cacheKey) => {
      const parsed =
        safeParse(
          window.localStorage.getItem(
            cacheKey
          )
        );

      const currentItems =
        getSummaryCacheItems(parsed);

      const exists =
        currentItems.some((item) => {
          return (
            String(item?.id) ===
            trailerId
          );
        });

      const nextItems =
        exists
          ? currentItems.map((item) => {
              return (
                String(item?.id) ===
                trailerId
              )
                ? {
                    ...item,
                    ...trailer,
                  }
                : item;
            })
          : [
              trailer,
              ...currentItems,
            ];

      nextItems.sort((first, second) => {
        return String(
          first?.trailerPlate ||
          first?.trailerNumber ||
          ""
        ).localeCompare(
          String(
            second?.trailerPlate ||
            second?.trailerNumber ||
            ""
          ),
          undefined,
          {
            sensitivity: "base",
          }
        );
      });

      writeSummaryCacheItems(
        cacheKey,
        parsed,
        nextItems
      );
    });
  } catch {
    // O detalhe já ficou guardado mesmo que a lista não possa ser atualizada.
  }

  window.dispatchEvent(
    new CustomEvent(
      TRAILER_SUMMARY_UPDATED_EVENT,
      {
        detail: {
          trailer,
        },
      }
    )
  );
}

function daysUntilDate(value) {
  if (!value) {
    return null;
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return null;
  }

  const today =
    new Date();

  today.setHours(
    0,
    0,
    0,
    0
  );

  date.setHours(
    0,
    0,
    0,
    0
  );

  return Math.ceil(
    (
      date.getTime() -
      today.getTime()
    ) /
    DAY_MS
  );
}

function getDocumentStatus(
  file,
  expiry,
  t
) {
  if (!file) {
    return {
      tone: "missing",

      label: t(
        "documents.status.missing",
        {
          defaultValue: "Em falta",
        }
      ),
    };
  }

  const remainingDays =
    daysUntilDate(expiry);

  if (
    remainingDays !== null &&
    remainingDays < 0
  ) {
    return {
      tone: "expired",

      label: t(
        "documents.status.expired",
        {
          defaultValue: "Expirado",
        }
      ),
    };
  }

  if (
    remainingDays !== null &&
    remainingDays <=
      DOCUMENT_EXPIRY_WARNING_DAYS
  ) {
    return {
      tone: "expiring",

      label: t(
        "documents.status.expiring",
        {
          defaultValue: "Em breve",
        }
      ),
    };
  }

  return {
    tone: "loaded",

    label: t(
      "documents.status.loaded",
      {
        defaultValue: "Carregado",
      }
    ),
  };
}

/* =========================================================
   SELECT STYLES
========================================================= */

function getTrailerSelectStyles(
  hasError
) {
  return {
    container: (base) => ({
      ...base,
      width: "100%",
    }),

    control: (base) => ({
      ...base,

      width: "100%",
      minHeight: 25,
      height: 25,

      padding: 0,

      border: "none",
      borderRadius: 0,

      backgroundColor: "transparent",
      boxShadow: "none",

      cursor: "pointer",

      ":hover": {
        border: "none",
      },
    }),

    valueContainer: (base) => ({
      ...base,
      height: 25,
      padding: 0,
    }),

    input: (base) => ({
      ...base,

      margin: 0,
      padding: 0,

      color: "#f1f3f7",
    }),

    singleValue: (base) => ({
      ...base,

      margin: 0,

      color: "#f1f3f7",

      fontSize: 12,
      fontWeight: 650,
    }),

    placeholder: (base) => ({
      ...base,

      margin: 0,

      color: "#747b87",

      fontSize: 12,
    }),

    indicatorsContainer: (base) => ({
      ...base,

      height: 25,

      display: "flex",
      alignItems: "center",

      transform: "translateY(-2px)",
    }),

    indicatorSeparator: (base) => ({
      ...base,

      height: 18,

      marginTop: 0,
      marginBottom: 0,

      alignSelf: "center",

      backgroundColor: hasError
        ? "rgba(239, 102, 107, 0.46)"
        : "rgba(255, 255, 255, 0.1)",
    }),

    dropdownIndicator: (base, state) => ({
      ...base,

      height: 25,
      padding: "0 6px",

      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      color: hasError
        ? "#ef777c"
        : state.isFocused
        ? "#b8c3d3"
        : "#858d99",

      ":hover": {
        color: hasError
          ? "#ff9ca0"
          : "#d8dde5",
      },
    }),

    clearIndicator: (base) => ({
      ...base,

      height: 25,
      padding: "0 6px",

      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      color: "#757d89",

      ":hover": {
        color: "#e88d96",
      },
    }),

    menu: (base) => ({
      ...base,

      zIndex: 9999,

      marginTop: 8,
      overflow: "hidden",

      border:
        "1px solid rgba(255, 255, 255, 0.1)",

      borderRadius: 10,

      backgroundColor: "#111318",

      boxShadow:
        "0 20px 48px rgba(0, 0, 0, 0.48)",
    }),

    menuPortal: (base) => ({
      ...base,
      zIndex: 99999,
    }),

    menuList: (base) => ({
      ...base,

      maxHeight: 260,
      padding: 5,
    }),

    option: (base, state) => ({
      ...base,

      margin: "2px 0",
      padding: "8px 10px",

      borderRadius: 7,

      cursor: "pointer",

      color: state.isSelected
        ? "#ffffff"
        : "#c8ced8",

      backgroundColor: state.isSelected
        ? "rgba(38, 116, 231, 0.28)"
        : state.isFocused
        ? "rgba(255, 255, 255, 0.055)"
        : "transparent",

      fontSize: 11,

      ":active": {
        backgroundColor:
          "rgba(38, 116, 231, 0.22)",
      },
    }),
  };
}

/* =========================================================
   SMALL COMPONENTS
========================================================= */

function SectionHeading({
  number,
  title,
}) {
  return (
    <div className="edit-trailer-sectionHeading">
      <h2>
        {number}. {title}
      </h2>
    </div>
  );
}

function FieldCard({
  label,
  icon,
  error,
  className = "",
  children,
}) {
  return (
    <div
      className={[
        "edit-trailer-fieldCard",
        error ? "has-error" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span className="edit-trailer-fieldCard-icon">
        {icon}
      </span>

      <div className="edit-trailer-fieldCard-body">
        <div className="edit-trailer-fieldCard-label">
          {label}
        </div>

        <div className="edit-trailer-fieldCard-control">
          {children}
        </div>

        {error ? (
          <div className="edit-trailer-fieldCard-error">
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DocumentStatusPill({
  status,
}) {
  return (
    <span
      className={[
        "edit-trailer-documentStatus",
        `edit-trailer-documentStatus--${status.tone}`,
      ].join(" ")}
    >
      {status.label}
    </span>
  );
}

function DateControl({
  id,
  name,
  value,
  onChange,
  hasError,
  label,
}) {
  const inputRef =
    useRef(null);

  const openCalendar = () => {
    const input =
      inputRef.current;

    if (!input) {
      return;
    }

    input.focus({
      preventScroll: true,
    });

    try {
      if (
        typeof input.showPicker ===
        "function"
      ) {
        input.showPicker();
        return;
      }
    } catch {
      // Browser sem suporte para showPicker.
    }

    input.click();
  };

  return (
    <div
      className={[
        "edit-trailer-dateControl",
        hasError ? "has-error" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="date"
        value={value || ""}
        onChange={onChange}
      />

      <button
        type="button"
        className="edit-trailer-dateControl-button"
        aria-label={label}
        title={label}
        onMouseDown={(event) => {
          event.preventDefault();
        }}
        onClick={openCalendar}
      >
        <FaCalendarAlt />
      </button>
    </div>
  );
}

function SidebarDocumentRow({
  title,
  status,
}) {
  return (
    <div className="edit-trailer-sidebarDocumentRow">
      <div className="edit-trailer-sidebarDocumentName">
        <FaFileAlt />

        <span title={title}>
          {title}
        </span>
      </div>

      <DocumentStatusPill
        status={status}
      />
    </div>
  );
}

function DocumentCard({
  fieldName,
  title,
  file,
  expiryField,
  expiryValue,
  status,
  fileError,
  expiryError,
  onFileChange,
  onDateChange,
  onView,
  t,
}) {
  const inputId =
    `edit-trailer-file-${fieldName}`;

  const currentFileName =
    fileName(file);

  const openFilePicker = () => {
    document
      .getElementById(inputId)
      ?.click();
  };

  return (
    <article
      className={[
        "edit-trailer-documentCard",
        fileError || expiryError
          ? "has-error"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <header className="edit-trailer-documentCard-header">
        <div className="edit-trailer-documentCard-identity">
          <span className="edit-trailer-documentCard-icon">
            <FaFileAlt />
          </span>

          <div className="edit-trailer-documentCard-titleWrap">
            <h3 title={title}>
              {title}
            </h3>

            <span
              className="edit-trailer-documentCard-fileName"
              title={
                currentFileName ||
                t(
                  "upload.no_file",
                  {
                    defaultValue: "Sem ficheiro",
                  }
                )
              }
            >
              {currentFileName ||
                t(
                  "upload.no_file",
                  {
                    defaultValue: "Sem ficheiro",
                  }
                )}
            </span>
          </div>
        </div>

        <button
          type="button"
          className="edit-trailer-documentCard-change"
          onClick={openFilePicker}
        >
          {file
            ? t(
                "buttons.change",
                {
                  defaultValue: "Alterar",
                }
              )
            : t(
                "buttons.upload",
                {
                  defaultValue: "Carregar",
                }
              )}
        </button>
      </header>

      <div className="edit-trailer-documentCard-middle">
        <DocumentStatusPill
          status={status}
        />

        {file ? (
          <button
            type="button"
            className="edit-trailer-documentCard-fileLink"
            title={currentFileName}
            onClick={() => {
              onView({
                file,
                title:
                  currentFileName ||
                  title,
              });
            }}
          >
            <FaFileAlt />

            <span>
              {currentFileName}
            </span>
          </button>
        ) : (
          <button
            type="button"
            className="edit-trailer-documentCard-empty"
            onClick={openFilePicker}
          >
            <FaUpload />

            <span>
              {t(
                "upload.prompt",
                {
                  defaultValue:
                    "Carrega aqui o teu ficheiro.",
                }
              )}
            </span>
          </button>
        )}
      </div>

      <div className="edit-trailer-documentCard-divider" />

      <div className="edit-trailer-documentCard-validity">
        <span>
          {t(
            "labels.expiry_short",
            {
              defaultValue:
                "Validade",
            }
          )}
        </span>

        <DateControl
          id={`edit-trailer-date-${expiryField}`}
          name={expiryField}
          value={expiryValue}
          onChange={onDateChange}
          hasError={Boolean(expiryError)}
          label={t(
            "labels.open_calendar",
            {
              defaultValue:
                "Abrir calendário",
            }
          )}
        />
      </div>

      {fileError ? (
        <div className="edit-trailer-documentCard-error">
          {fileError}
        </div>
      ) : null}

      {expiryError ? (
        <div className="edit-trailer-documentCard-error">
          {expiryError}
        </div>
      ) : null}

      <input
        type="file"
        id={inputId}
        name={fieldName}
        accept="image/jpeg,image/png,image/webp,application/pdf"
        onChange={onFileChange}
        className="edit-trailer-hiddenFileInput"
      />
    </article>
  );
}

/* =========================================================
   COMPONENT
========================================================= */

function EditTrailer() {
  const { t } =
    useTranslation("edit_trailer");

  const { trailerId } =
    useParams();

  const navigate =
    useNavigate();

  const location =
    useLocation();

  const prefill =
    location.state?.prefill ??
    null;

  const cachedPrefill =
    useMemo(() => {
      return readTrailerDetailsCache(
        trailerId
      );
    }, [
      trailerId,
    ]);

  const seed =
    prefill ||
    cachedPrefill ||
    null;

  const [
    trailer,
    setTrailer,
  ] = useState(() => {
    return seed
      ? normalizeIncomingTrailer(seed)
      : EMPTY_TRAILER;
  });

  const [
    invalidFields,
    setInvalidFields,
  ] = useState({});

  const [
    errorMessage,
    setErrorMessage,
  ] = useState("");

  const [
    successPopupOpen,
    setSuccessPopupOpen,
  ] = useState(false);

  const [
    viewerFile,
    setViewerFile,
  ] = useState(null);

  const [
    previewOpen,
    setPreviewOpen,
  ] = useState(false);

  const [
    isSaving,
    setIsSaving,
  ] = useState(false);

  const dirtyFieldsRef =
    useRef(new Set());

  const clearFieldError =
    useCallback((name) => {
      setInvalidFields((previous) => {
        if (!previous[name]) {
          return previous;
        }

        const next = {
          ...previous,
        };

        delete next[name];

        return next;
      });
    }, []);

  const markFieldDirty =
    useCallback((name) => {
      dirtyFieldsRef.current.add(name);
    }, []);

  const typeOptions =
    useMemo(() => {
      return TRAILER_TYPES.map((type) => ({
        value: type,

        label: t(
          `types.${type}`,
          {
            defaultValue: type,
          }
        ),
      }));
    }, [
      t,
    ]);

  const monthOptions =
    useMemo(() => {
      return Array.from(
        {
          length: 12,
        },
        (_, index) => ({
          value:
            index + 1,

          label: t(
            `months.${index + 1}`,
            {
              defaultValue:
                new Date(
                  0,
                  index
                ).toLocaleString(
                  "default",
                  {
                    month: "long",
                  }
                ),
            }
          ),
        })
      );
    }, [
      t,
    ]);

  const yearOptions =
    useMemo(() => {
      const currentYear =
        new Date().getFullYear();

      return Array.from(
        {
          length:
            currentYear -
            1980 +
            1,
        },
        (_, index) => {
          const year =
            currentYear -
            index;

          return {
            value: year,
            label: String(year),
          };
        }
      );
    }, []);

  const selectedType =
    useMemo(() => {
      return (
        typeOptions.find((option) => {
          return (
            option.value ===
            trailer.type
          );
        }) ||
        null
      );
    }, [
      trailer.type,
      typeOptions,
    ]);

  const selectedMonth =
    useMemo(() => {
      return (
        monthOptions.find((option) => {
          return (
            Number(option.value) ===
            Number(trailer.month)
          );
        }) ||
        null
      );
    }, [
      monthOptions,
      trailer.month,
    ]);

  const selectedYear =
    useMemo(() => {
      return (
        yearOptions.find((option) => {
          return (
            Number(option.value) ===
            Number(trailer.year)
          );
        }) ||
        null
      );
    }, [
      trailer.year,
      yearOptions,
    ]);

  const isActive =
    useMemo(() => {
      const status =
        String(
          trailer.status ||
          "ACTIVE"
        ).toUpperCase();

      return ![
        "INACTIVE",
        "ARCHIVED",
        "DELETED",
        "CANCELLED",
      ].includes(status);
    }, [
      trailer.status,
    ]);

  useEffect(() => {
    let cancelled =
      false;

    const fetchTrailer =
      async () => {
        try {
          const { data } =
            await axios.get(
              `/trailers/${trailerId}`,
              {
                params: {
                  _fresh:
                    Date.now(),
                },

                headers: {
                  "Cache-Control":
                    "no-cache",

                  Pragma:
                    "no-cache",
                },
              }
            );

          if (cancelled) {
            return;
          }

          writeTrailerDetailsCache(
            trailerId,
            data
          );

          const incoming =
            normalizeIncomingTrailer(
              data
            );

          setTrailer((previous) => {
            const next = {
              ...previous,
            };

            Object.entries(
              incoming
            ).forEach(([
              key,
              value,
            ]) => {
              if (
                dirtyFieldsRef.current.has(
                  key
                )
              ) {
                return;
              }

              next[key] =
                value;
            });

            return next;
          });
        } catch (error) {
          if (
            error?.name ===
              "CanceledError" ||
            error?.code ===
              "ERR_CANCELED"
          ) {
            return;
          }

          console.error(
            "[EditTrailer] loading failed:",
            error
          );

          setErrorMessage(
            t(
              "errors.load_fail",
              {
                defaultValue:
                  "Falha ao carregar os dados do reboque. Tenta novamente.",
              }
            )
          );
        }
      };

    fetchTrailer();

    return () => {
      cancelled =
        true;
    };
  }, [
    trailerId,
    t,
  ]);

  useEffect(() => {
    const modalOpen =
      Boolean(
        viewerFile ||
        previewOpen ||
        successPopupOpen
      );

    if (!modalOpen) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    const onKeyDown =
      (event) => {
        if (
          event.key !==
          "Escape"
        ) {
          return;
        }

        if (viewerFile) {
          setViewerFile(null);
          return;
        }

        if (previewOpen) {
          setPreviewOpen(false);
        }
      };

    window.addEventListener(
      "keydown",
      onKeyDown
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        onKeyDown
      );
    };
  }, [
    previewOpen,
    successPopupOpen,
    viewerFile,
  ]);

  const handleChange =
    useCallback(
      (event) => {
        const {
          name,
          value,
        } = event.target;

        const nextValue =
          name === "trailerPlate"
            ? normalizePlate(value)
            : value;

        markFieldDirty(name);
        clearFieldError(name);
        setErrorMessage("");

        setTrailer((previous) => ({
          ...previous,
          [name]: nextValue,
        }));
      },
      [
        clearFieldError,
        markFieldDirty,
      ]
    );

  const handleSelectChange =
    useCallback(
      (
        name,
        option
      ) => {
        const value =
          option?.value ??
          "";

        markFieldDirty(name);
        clearFieldError(name);
        setErrorMessage("");

        setTrailer((previous) => ({
          ...previous,
          [name]: value,
        }));
      },
      [
        clearFieldError,
        markFieldDirty,
      ]
    );

  const handleFileChange =
    useCallback(
      (event) => {
        const {
          name,
          files,
        } = event.target;

        const selectedFile =
          files?.[0] ||
          null;

        if (!selectedFile) {
          return;
        }

        markFieldDirty(name);
        clearFieldError(name);
        setErrorMessage("");

        setTrailer((previous) => ({
          ...previous,
          [name]:
            selectedFile,
        }));
      },
      [
        clearFieldError,
        markFieldDirty,
      ]
    );

  const validateFields =
    useCallback(() => {
      const nextErrors = {};

      const numericFields = [
        "height",
        "width",
        "length",
        "weight",
        "numberOfAxles",
        "numberOfStraps",
        "numberOfRatchets",
        "numberOfWoodenBeams",
        "numberOfEdgeProtectors",
        "numberOfAntiSlippingMattresses",
        "numberOfSidebars",
        "numberOfFireExtinguishers",
      ];

      numericFields.forEach((field) => {
        const value =
          trailer[field];

        if (
          value !== "" &&
          value !== null &&
          value !== undefined &&
          Number(value) < 0
        ) {
          nextErrors[field] =
            t(
              "errors.non_negative",
              {
                defaultValue:
                  "O valor não pode ser negativo.",
              }
            );
        }
      });

      setInvalidFields(
        nextErrors
      );

      return (
        Object.keys(
          nextErrors
        ).length ===
        0
      );
    }, [
      t,
      trailer,
    ]);

  const documentItems =
    useMemo(() => {
      const items = [
        {
          key: "inspection",

          title: t(
            "labels.inspection_photo",
            {
              defaultValue:
                "Inspeção (Foto/Doc.)",
            }
          ),

          expiryField:
            "inspectionExpiry",
        },

        {
          key:
            "vehicleRegistrationCertificate",

          title: t(
            "labels.vehicle_registration_certificate",
            {
              defaultValue:
                "Certificado de Matrícula",
            }
          ),

          expiryField:
            "vehicleRegistrationCertificateExpiry",
        },

        {
          key:
            "trailerInsurance",

          title: t(
            "labels.trailer_insurance",
            {
              defaultValue:
                "Seguro do Reboque",
            }
          ),

          expiryField:
            "trailerInsuranceExpiry",
        },
      ];

      return items.map((item) => ({
        ...item,

        file:
          trailer[item.key],

        expiryValue:
          trailer[item.expiryField],

        status:
          getDocumentStatus(
            trailer[item.key],
            trailer[item.expiryField],
            t
          ),
      }));
    }, [
      t,
      trailer,
    ]);

  const monthLabel =
    selectedMonth?.label ||
    "—";

  const yearLabel =
    selectedYear?.label ||
    trailer.year ||
    "—";

  const typeLabel =
    selectedType?.label ||
    trailer.type ||
    "—";

  const brandTypeLabel =
    [
      trailer.brand,
      typeLabel !== "—"
        ? typeLabel
        : "",
    ]
      .filter(Boolean)
      .join(" • ") ||
    "—";

  const dimensionSummary =
    [
      trailer.length
        ? `${trailer.length} m`
        : "—",

      trailer.width
        ? `${trailer.width} m`
        : "—",

      trailer.height
        ? `${trailer.height} m`
        : "—",
    ].join(" × ");

  const sidebarSpecifications =
    useMemo(() => {
      return [
        {
          icon:
            FaCalendarAlt,

          label: t(
            "labels.year",
            {
              defaultValue: "Ano",
            }
          ),

          value:
            yearLabel,
        },

        {
          icon:
            FaCalendarAlt,

          label: t(
            "labels.month",
            {
              defaultValue: "Mês",
            }
          ),

          value:
            monthLabel,
        },

        {
          icon:
            FaRulerCombined,

          label: t(
            "sections.dimensions",
            {
              defaultValue:
                "Dimensões",
            }
          ),

          value:
            dimensionSummary,
        },

        {
          icon:
            FaWeightHanging,

          label: t(
            "labels.weight_tons",
            {
              defaultValue:
                "Peso",
            }
          ),

          value:
            trailer.weight
              ? `${trailer.weight} t`
              : "—",
        },

        {
          icon:
            FaRoad,

          label: t(
            "labels.number_of_axles",
            {
              defaultValue:
                "N.º de Eixos",
            }
          ),

          value:
            trailer.numberOfAxles ||
            "0",
        },

        {
          icon:
            FaLink,

          label: t(
            "labels.number_of_straps",
            {
              defaultValue:
                "N.º de Cintas",
            }
          ),

          value:
            trailer.numberOfStraps ||
            "0",
        },

        {
          icon:
            FaLink,

          label: t(
            "labels.number_of_ratchets",
            {
              defaultValue:
                "N.º de Catracas",
            }
          ),

          value:
            trailer.numberOfRatchets ||
            "0",
        },

        {
          icon:
            FaBars,

          label: t(
            "labels.number_of_wooden_beams",
            {
              defaultValue:
                "N.º de Vigas de Madeira",
            }
          ),

          value:
            trailer.numberOfWoodenBeams ||
            "0",
        },

        {
          icon:
            FaShieldAlt,

          label: t(
            "labels.number_of_edge_protectors",
            {
              defaultValue:
                "N.º de Protetores de Canto",
            }
          ),

          value:
            trailer.numberOfEdgeProtectors ||
            "0",
        },

        {
          icon:
            FaLayerGroup,

          label: t(
            "labels.number_of_anti_slip_mats",
            {
              defaultValue:
                "N.º de Tapetes Antiderrapantes",
            }
          ),

          value:
            trailer.numberOfAntiSlippingMattresses ||
            "0",
        },

        {
          icon:
            FaBars,

          label: t(
            "labels.number_of_sidebars",
            {
              defaultValue:
                "N.º de Barras Laterais",
            }
          ),

          value:
            trailer.numberOfSidebars ||
            "0",
        },

        {
          icon:
            FaFireExtinguisher,

          label: t(
            "labels.number_of_fire_extinguishers",
            {
              defaultValue:
                "N.º de Extintores",
            }
          ),

          value:
            trailer.numberOfFireExtinguishers ||
            "0",
        },
      ];
    }, [
      dimensionSummary,
      monthLabel,
      t,
      trailer,
      yearLabel,
    ]);

  const handleSubmit =
    async (event) => {
      event.preventDefault();

      if (isSaving) {
        return;
      }

      setErrorMessage("");

      if (!validateFields()) {
        setErrorMessage(
          t(
            "errors.check_fields",
            {
              defaultValue:
                "Verifica os campos assinalados.",
            }
          )
        );

        return;
      }

      setIsSaving(true);

      const payload =
        new FormData();

      payload.append(
        "trailerPlate",
        trailer.trailerPlate ??
        ""
      );

      payload.append(
        "brand",
        trailer.brand ??
        ""
      );

      payload.append(
        "type",
        trailer.type ??
        ""
      );

      payload.append(
        "month",
        trailer.month ??
        ""
      );

      payload.append(
        "year",
        trailer.year ??
        ""
      );

      payload.append(
        "status",
        trailer.status ??
        ""
      );

      payload.append(
        "height",
        trailer.height ??
        ""
      );

      payload.append(
        "width",
        trailer.width ??
        ""
      );

      payload.append(
        "length",
        trailer.length ??
        ""
      );

      payload.append(
        "weight",
        trailer.weight ??
        ""
      );

      payload.append(
        "numberOfAxles",
        trailer.numberOfAxles ??
        ""
      );

      payload.append(
        "numberOfStraps",
        trailer.numberOfStraps ??
        ""
      );

      payload.append(
        "numberOfRatchets",
        trailer.numberOfRatchets ??
        ""
      );

      payload.append(
        "numberOfWoodenBeams",
        trailer.numberOfWoodenBeams ??
        ""
      );

      payload.append(
        "numberOfEdgeProtectors",
        trailer.numberOfEdgeProtectors ??
        ""
      );

      payload.append(
        "numberOfAntiSlipMats",
        trailer.numberOfAntiSlippingMattresses ??
        ""
      );

      payload.append(
        "numberOfSidebars",
        trailer.numberOfSidebars ??
        ""
      );

      payload.append(
        "numberOfFireExtinguishers",
        trailer.numberOfFireExtinguishers ??
        ""
      );

      payload.append(
        "inspectionExpiry",
        trailer.inspectionExpiry ??
        ""
      );

      payload.append(
        "vehicleRegistrationCertificateExpiry",
        trailer.vehicleRegistrationCertificateExpiry ??
        ""
      );

      payload.append(
        "trailerInsuranceExpiry",
        trailer.trailerInsuranceExpiry ??
        ""
      );

      if (
        typeof File !== "undefined" &&
        trailer.inspection instanceof File
      ) {
        payload.append(
          "inspection",
          trailer.inspection
        );
      }

      if (
        typeof File !== "undefined" &&
        trailer.vehicleRegistrationCertificate instanceof File
      ) {
        payload.append(
          "vehicleRegistrationCertificate",
          trailer.vehicleRegistrationCertificate
        );
      }

      if (
        typeof File !== "undefined" &&
        trailer.trailerInsurance instanceof File
      ) {
        payload.append(
          "trailerInsurance",
          trailer.trailerInsurance
        );
      }

      try {
        const response =
          await axios.put(
            `/trailers/${trailerId}`,
            payload,
            {
              headers: {
                "Content-Type":
                  "multipart/form-data",
              },
            }
          );

        const responsePayload =
          unwrapTrailerResponse(
            response.data
          );

        const previousCached =
          readTrailerDetailsCache(
            trailerId
          ) ||
          {};

        const optimisticTrailer = {
          ...previousCached,
          ...responsePayload,

          id:
            responsePayload.id ??
            previousCached.id ??
            Number(trailerId),

          trailerPlate:
            trailer.trailerPlate,

          trailerNumber:
            trailer.trailerPlate,

          brand:
            trailer.brand,

          type:
            trailer.type,

          month:
            trailer.month,

          Month:
            trailer.month,

          year:
            trailer.year,

          status:
            trailer.status ||
            "ACTIVE",

          height:
            trailer.height,

          width:
            trailer.width,

          length:
            trailer.length,

          weight:
            trailer.weight,

          numberOfAxles:
            trailer.numberOfAxles,

          numberOfStraps:
            trailer.numberOfStraps,

          numberOfRatchets:
            trailer.numberOfRatchets,

          numberOfWoodenBeams:
            trailer.numberOfWoodenBeams,

          numberOfEdgeProtectors:
            trailer.numberOfEdgeProtectors,

          numberOfAntiSlippingMattresses:
            trailer.numberOfAntiSlippingMattresses,

          numberOfAntiSlipMats:
            trailer.numberOfAntiSlippingMattresses,

          numberOfSidebars:
            trailer.numberOfSidebars,

          numberOfFireExtinguishers:
            trailer.numberOfFireExtinguishers,

          inspectionExpiry:
            trailer.inspectionExpiry,

          vehicleRegistrationCertificateExpiry:
            trailer.vehicleRegistrationCertificateExpiry,

          trailerInsuranceExpiry:
            trailer.trailerInsuranceExpiry,

          inspection:
            responsePayload.inspection ??
            responsePayload.Inspection ??
            previousCached.inspection ??
            previousCached.Inspection ??
            null,

          vehicleRegistrationCertificate:
            responsePayload.vehicleRegistrationCertificate ??
            responsePayload.VehicleRegistrationCertificate ??
            previousCached.vehicleRegistrationCertificate ??
            previousCached.VehicleRegistrationCertificate ??
            null,

          trailerInsurance:
            responsePayload.trailerInsurance ??
            responsePayload.TrailerInsurance ??
            previousCached.trailerInsurance ??
            previousCached.TrailerInsurance ??
            null,
        };

        writeTrailerDetailsCache(
          trailerId,
          optimisticTrailer
        );

        patchTrailerSummaryCaches(
          optimisticTrailer
        );

        /*
         * Atualização canónica em segundo plano:
         * obtém as URLs finais dos ficheiros enviados
         * sem atrasar o popup de sucesso.
         */
        axios
          .get(
            `/trailers/${trailerId}`,
            {
              params: {
                _fresh:
                  Date.now(),
              },

              headers: {
                "Cache-Control":
                  "no-cache",

                Pragma:
                  "no-cache",
              },
            }
          )
          .then(({ data }) => {
            const canonicalTrailer = {
              ...data,

              id:
                data?.id ??
                Number(trailerId),

              trailerPlate:
                data?.trailerPlate ??
                data?.trailerNumber ??
                trailer.trailerPlate,
            };

            writeTrailerDetailsCache(
              trailerId,
              canonicalTrailer
            );

            patchTrailerSummaryCaches(
              canonicalTrailer
            );
          })
          .catch((error) => {
            console.warn(
              "[EditTrailer] canonical cache refresh failed:",
              error
            );
          });

        setSuccessPopupOpen(
          true
        );
      } catch (error) {
        console.error(
          "[EditTrailer] update failed:",
          error?.response?.data ||
          error
        );

        const backendErrors =
          error?.response?.data?.errors;

        if (
          Array.isArray(
            backendErrors
          )
        ) {
          const mappedErrors = {};

          backendErrors.forEach((item) => {
            const field =
              item?.param ||
              item?.path ||
              item?.field;

            if (!field) {
              return;
            }

            const normalizedField =
              field ===
              "numberOfAntiSlipMats"
                ? "numberOfAntiSlippingMattresses"
                : field;

            mappedErrors[normalizedField] =
              item?.msg ||
              item?.message ||
              t(
                "errors.invalid_field",
                {
                  defaultValue:
                    "Valor inválido.",
                }
              );
          });

          if (
            Object.keys(
              mappedErrors
            ).length >
            0
          ) {
            setInvalidFields(
              mappedErrors
            );
          }
        }

        setErrorMessage(
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          t(
            "errors.update_fail",
            {
              defaultValue:
                "Falha ao atualizar o reboque. Tenta novamente.",
            }
          )
        );
      } finally {
        setIsSaving(false);
      }
    };

  const viewerData =
    useMemo(() => {
      if (!viewerFile?.file) {
        return null;
      }

      const href =
        buildFileUrl(
          viewerFile.file
        );

      const name =
        cleanFileName(
          viewerFile.title ||
          fileName(
            viewerFile.file
          ),
          t(
            "upload.view_file",
            {
              defaultValue:
                "ficheiro",
            }
          )
        );

      return {
        href,
        name,

        file:
          viewerFile.file,

        isPdf:
          isPdfFile(
            viewerFile.file
          ),

        isImage:
          isImageFile(
            viewerFile.file
          ),
      };
    }, [
      t,
      viewerFile,
    ]);

  useEffect(() => {
    return () => {
      if (
        viewerData?.href?.startsWith(
          "blob:"
        )
      ) {
        try {
          URL.revokeObjectURL(
            viewerData.href
          );
        } catch {
          // URL já libertado.
        }
      }
    };
  }, [
    viewerData?.href,
  ]);

  const handleDownloadViewerFile =
    async () => {
      if (!viewerData?.href) {
        return;
      }

      const fallbackName =
        cleanFileName(
          viewerData.name,
          "document"
        );

      try {
        let blob;
        let contentType = "";

        if (
          typeof File !== "undefined" &&
          (
            viewerData.file instanceof File ||
            viewerData.file instanceof Blob
          )
        ) {
          blob =
            viewerData.file;

          contentType =
            viewerData.file.type ||
            "";
        } else {
          const response =
            await axios.get(
              "/company-details/download-file",
              {
                responseType:
                  "blob",

                params: {
                  url:
                    viewerData.href,

                  filename:
                    fallbackName,
                },
              }
            );

          blob =
            response.data;

          contentType =
            String(
              response.headers?.["content-type"] ||
              blob?.type ||
              ""
            );
        }

        const lowerType =
          contentType.toLowerCase();

        if (
          lowerType.includes(
            "application/json"
          ) ||
          lowerType.includes(
            "text/html"
          ) ||
          lowerType.includes(
            "application/xml"
          ) ||
          lowerType.includes(
            "text/xml"
          )
        ) {
          alert(
            t(
              "errors.invalid_file_response",
              {
                defaultValue:
                  "O servidor devolveu uma resposta inválida em vez do ficheiro.",
              }
            )
          );

          return;
        }

        if (
          !blob ||
          blob.size ===
            0
        ) {
          alert(
            t(
              "errors.empty_file",
              {
                defaultValue:
                  "O ficheiro descarregado está vazio.",
              }
            )
          );

          return;
        }

        const blobUrl =
          window.URL.createObjectURL(
            blob
          );

        const link =
          document.createElement(
            "a"
          );

        link.href =
          blobUrl;

        link.download =
          fallbackName;

        link.style.display =
          "none";

        document.body.appendChild(
          link
        );

        link.click();
        link.remove();

        window.setTimeout(() => {
          window.URL.revokeObjectURL(
            blobUrl
          );
        }, 1000);
      } catch (error) {
        console.error(
          "[EditTrailer] download failed:",
          error
        );

        alert(
          t(
            "errors.download_fail",
            {
              defaultValue:
                "Não foi possível descarregar o ficheiro automaticamente.",
            }
          )
        );
      }
    };

  const closeSuccessPopup = () => {
    setSuccessPopupOpen(false);

    navigate(
      "/trucks?view=trailers"
    );
  };

  return (
    <div className="edit-trailer-page">
      <div className="edit-trailer-shell">
        <header className="edit-trailer-topbar">
          <button
            type="button"
            className="edit-trailer-backButton"
            onClick={() => {
              navigate(
                "/trucks?view=trailers"
              );
            }}
          >
            <FaArrowLeft />

            <span>
              {t(
                "back",
                {
                  defaultValue:
                    "Voltar",
                }
              )}
            </span>
          </button>

          <div className="edit-trailer-topbar-title">
            <h1>
              {t(
                "title",
                {
                  defaultValue:
                    "Editar Reboque",
                }
              )}
            </h1>

            <p>
              {t(
                "page.subtitle",
                {
                  defaultValue:
                    "Atualize a informação, dimensões, equipamento e documentos do reboque.",
                }
              )}
            </p>
          </div>

          <div className="edit-trailer-topbar-actions">
            <button
              type="button"
              className="edit-trailer-previewButton"
              onClick={() => {
                setPreviewOpen(
                  true
                );
              }}
            >
              <FaEye />

              <span>
                {t(
                  "buttons.preview",
                  {
                    defaultValue:
                      "Pré-visualizar",
                  }
                )}
              </span>
            </button>

            <button
              type="submit"
              form="edit-trailer-form"
              className="edit-trailer-primarySaveButton"
              disabled={isSaving}
            >
              <FaSave />

              <span>
                {isSaving
                  ? t(
                      "buttons.saving",
                      {
                        defaultValue:
                          "A guardar...",
                      }
                    )
                  : t(
                      "buttons.save_changes",
                      {
                        defaultValue:
                          "Guardar Alterações",
                      }
                    )}
              </span>
            </button>
          </div>
        </header>

        {errorMessage ? (
          <div className="edit-trailer-globalError">
            <FaExclamationTriangle />

            <span>
              {errorMessage}
            </span>
          </div>
        ) : null}

        <div className="edit-trailer-layout">
          <aside className="edit-trailer-sidebar">
            <div className="edit-trailer-sidebarCard">
              <div className="edit-trailer-sidebarIdentity">
                <span className="edit-trailer-sidebarEyebrow">
                  {t(
                    "labels.trailer_plate",
                    {
                      defaultValue:
                        "Matrícula do Reboque",
                    }
                  )}
                </span>

                <h2>
                  {trailer.trailerPlate ||
                    "—"}
                </h2>

                <p>
                  {brandTypeLabel}
                </p>

                <span
                  className={[
                    "edit-trailer-vehicleStatus",
                    isActive
                      ? "is-active"
                      : "is-inactive",
                  ].join(" ")}
                >
                  <span />

                  {isActive
                    ? t(
                        "status.active",
                        {
                          defaultValue:
                            "Ativo",
                        }
                      )
                    : t(
                        "status.inactive",
                        {
                          defaultValue:
                            "Inativo",
                        }
                      )}
                </span>
              </div>

              <div className="edit-trailer-sidebarBlock">
                <div className="edit-trailer-sidebarBlockTitle">
                  {t(
                    "labels.trailer_type",
                    {
                      defaultValue:
                        "Tipo de Reboque",
                    }
                  )}
                </div>

                <div className="edit-trailer-sidebarType">
                  <FaRoad />

                  <span>
                    {typeLabel}
                  </span>
                </div>
              </div>

              <div className="edit-trailer-sidebarBlock">
                <div className="edit-trailer-sidebarBlockTitle">
                  {t(
                    "sections.documents",
                    {
                      defaultValue:
                        "Documentos",
                    }
                  )}
                </div>

                <div className="edit-trailer-sidebarDocuments">
                  {documentItems.map((item) => (
                    <SidebarDocumentRow
                      key={item.key}
                      title={item.title}
                      status={item.status}
                    />
                  ))}
                </div>
              </div>

              <div className="edit-trailer-sidebarBlock">
                <div className="edit-trailer-sidebarBlockTitle">
                  {t(
                    "sections.specifications",
                    {
                      defaultValue:
                        "Especificações",
                    }
                  )}
                </div>

                <div className="edit-trailer-sidebarSpecs">
                  {sidebarSpecifications.map((item) => {
                    const Icon =
                      item.icon;

                    return (
                      <div
                        key={item.label}
                        className="edit-trailer-sidebarSpecRow"
                      >
                        <span className="edit-trailer-sidebarSpecLabel">
                          <Icon />

                          <span>
                            {item.label}
                          </span>
                        </span>

                        <strong title={String(item.value)}>
                          {item.value}
                        </strong>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </aside>

          <main className="edit-trailer-main">
            <form
              id="edit-trailer-form"
              className="edit-trailer-dashboardForm"
              onSubmit={handleSubmit}
              encType="multipart/form-data"
            >
              <section className="edit-trailer-sectionCard">
                <SectionHeading
                  number="1"
                  title={t(
                    "sections.basic",
                    {
                      defaultValue:
                        "Informação Básica",
                    }
                  )}
                />

                <div className="edit-trailer-basicGrid">
                  <FieldCard
                    label={t(
                      "labels.trailer_plate",
                      {
                        defaultValue:
                          "Matrícula do Reboque",
                      }
                    )}
                    icon={<FaHashtag />}
                    error={invalidFields.trailerPlate}
                    className="edit-trailer-span-4"
                  >
                    <input
                      type="text"
                      name="trailerPlate"
                      value={trailer.trailerPlate}
                      onChange={handleChange}
                      className="edit-trailer-controlInput edit-trailer-controlInput--uppercase"
                      placeholder={t(
                        "placeholders.plate",
                        {
                          defaultValue:
                            "Introduz a matrícula...",
                        }
                      )}
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.trailer_brand",
                      {
                        defaultValue:
                          "Marca do Reboque",
                      }
                    )}
                    icon={<FaIndustry />}
                    error={invalidFields.brand}
                    className="edit-trailer-span-4"
                  >
                    <input
                      type="text"
                      name="brand"
                      value={trailer.brand}
                      onChange={handleChange}
                      className="edit-trailer-controlInput"
                      placeholder={t(
                        "placeholders.brand",
                        {
                          defaultValue:
                            "Introduz a marca...",
                        }
                      )}
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.trailer_type",
                      {
                        defaultValue:
                          "Tipo de Reboque",
                      }
                    )}
                    icon={<FaRoad />}
                    error={invalidFields.type}
                    className="edit-trailer-span-4"
                  >
                    <Select
                      name="type"
                      value={selectedType}
                      options={typeOptions}
                      onChange={(option) => {
                        handleSelectChange(
                          "type",
                          option
                        );
                      }}
                      placeholder={t(
                        "placeholders.select_type",
                        {
                          defaultValue:
                            "Seleciona o tipo de reboque",
                        }
                      )}
                      styles={getTrailerSelectStyles(
                        Boolean(
                          invalidFields.type
                        )
                      )}
                      isClearable
                      menuPortalTarget={
                        typeof document !==
                        "undefined"
                          ? document.body
                          : null
                      }
                      menuPosition="fixed"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.month",
                      {
                        defaultValue:
                          "Mês",
                      }
                    )}
                    icon={<FaCalendarAlt />}
                    error={invalidFields.month}
                    className="edit-trailer-span-6"
                  >
                    <Select
                      name="month"
                      value={selectedMonth}
                      options={monthOptions}
                      onChange={(option) => {
                        handleSelectChange(
                          "month",
                          option
                        );
                      }}
                      placeholder={t(
                        "placeholders.select_month",
                        {
                          defaultValue:
                            "Seleciona o mês",
                        }
                      )}
                      styles={getTrailerSelectStyles(
                        Boolean(
                          invalidFields.month
                        )
                      )}
                      isClearable
                      menuPortalTarget={
                        typeof document !==
                        "undefined"
                          ? document.body
                          : null
                      }
                      menuPosition="fixed"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.year",
                      {
                        defaultValue:
                          "Ano",
                      }
                    )}
                    icon={<FaCalendarAlt />}
                    error={invalidFields.year}
                    className="edit-trailer-span-6"
                  >
                    <Select
                      name="year"
                      value={selectedYear}
                      options={yearOptions}
                      onChange={(option) => {
                        handleSelectChange(
                          "year",
                          option
                        );
                      }}
                      placeholder={t(
                        "placeholders.select_year",
                        {
                          defaultValue:
                            "Seleciona o ano",
                        }
                      )}
                      styles={getTrailerSelectStyles(
                        Boolean(
                          invalidFields.year
                        )
                      )}
                      isClearable
                      menuPortalTarget={
                        typeof document !==
                        "undefined"
                          ? document.body
                          : null
                      }
                      menuPosition="fixed"
                    />
                  </FieldCard>
                </div>
              </section>

              <section className="edit-trailer-sectionCard">
                <SectionHeading
                  number="2"
                  title={t(
                    "sections.dimensions_equipment",
                    {
                      defaultValue:
                        "Dimensões & Equipamento",
                    }
                  )}
                />

                <div className="edit-trailer-equipmentGrid">
                  <FieldCard
                    label={t(
                      "labels.height_m",
                      {
                        defaultValue:
                          "Altura (m)",
                      }
                    )}
                    icon={<FaRulerVertical />}
                    error={invalidFields.height}
                    className="edit-trailer-span-3"
                  >
                    <input
                      name="height"
                      type="number"
                      min="0"
                      step="0.01"
                      value={trailer.height}
                      onChange={handleChange}
                      className="edit-trailer-controlInput"
                      placeholder="0.00"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.width_m",
                      {
                        defaultValue:
                          "Largura (m)",
                      }
                    )}
                    icon={<FaRulerHorizontal />}
                    error={invalidFields.width}
                    className="edit-trailer-span-3"
                  >
                    <input
                      name="width"
                      type="number"
                      min="0"
                      step="0.01"
                      value={trailer.width}
                      onChange={handleChange}
                      className="edit-trailer-controlInput"
                      placeholder="0.00"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.length_m",
                      {
                        defaultValue:
                          "Comprimento (m)",
                      }
                    )}
                    icon={<FaRulerCombined />}
                    error={invalidFields.length}
                    className="edit-trailer-span-3"
                  >
                    <input
                      name="length"
                      type="number"
                      min="0"
                      step="0.01"
                      value={trailer.length}
                      onChange={handleChange}
                      className="edit-trailer-controlInput"
                      placeholder="0.00"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.weight_tons",
                      {
                        defaultValue:
                          "Peso (toneladas)",
                      }
                    )}
                    icon={<FaWeightHanging />}
                    error={invalidFields.weight}
                    className="edit-trailer-span-3"
                  >
                    <input
                      name="weight"
                      type="number"
                      min="0"
                      step="0.01"
                      value={trailer.weight}
                      onChange={handleChange}
                      className="edit-trailer-controlInput"
                      placeholder="0.00"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_axles",
                      {
                        defaultValue:
                          "N.º de Eixos",
                      }
                    )}
                    icon={<FaRoad />}
                    error={invalidFields.numberOfAxles}
                    className="edit-trailer-span-3"
                  >
                    <input
                      name="numberOfAxles"
                      type="number"
                      min="0"
                      step="1"
                      value={trailer.numberOfAxles}
                      onChange={handleChange}
                      className="edit-trailer-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_straps",
                      {
                        defaultValue:
                          "N.º de Cintas",
                      }
                    )}
                    icon={<FaLink />}
                    error={invalidFields.numberOfStraps}
                    className="edit-trailer-span-3"
                  >
                    <input
                      name="numberOfStraps"
                      type="number"
                      min="0"
                      step="1"
                      value={trailer.numberOfStraps}
                      onChange={handleChange}
                      className="edit-trailer-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_ratchets",
                      {
                        defaultValue:
                          "N.º de Catracas",
                      }
                    )}
                    icon={<FaLink />}
                    error={invalidFields.numberOfRatchets}
                    className="edit-trailer-span-3"
                  >
                    <input
                      name="numberOfRatchets"
                      type="number"
                      min="0"
                      step="1"
                      value={trailer.numberOfRatchets}
                      onChange={handleChange}
                      className="edit-trailer-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_wooden_beams",
                      {
                        defaultValue:
                          "N.º de Vigas de Madeira",
                      }
                    )}
                    icon={<FaBars />}
                    error={invalidFields.numberOfWoodenBeams}
                    className="edit-trailer-span-3"
                  >
                    <input
                      name="numberOfWoodenBeams"
                      type="number"
                      min="0"
                      step="1"
                      value={trailer.numberOfWoodenBeams}
                      onChange={handleChange}
                      className="edit-trailer-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_edge_protectors",
                      {
                        defaultValue:
                          "N.º de Protetores de Canto",
                      }
                    )}
                    icon={<FaShieldAlt />}
                    error={invalidFields.numberOfEdgeProtectors}
                    className="edit-trailer-span-3"
                  >
                    <input
                      name="numberOfEdgeProtectors"
                      type="number"
                      min="0"
                      step="1"
                      value={trailer.numberOfEdgeProtectors}
                      onChange={handleChange}
                      className="edit-trailer-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_anti_slip_mats",
                      {
                        defaultValue:
                          "N.º de Tapetes Antiderrapantes",
                      }
                    )}
                    icon={<FaLayerGroup />}
                    error={invalidFields.numberOfAntiSlippingMattresses}
                    className="edit-trailer-span-3"
                  >
                    <input
                      name="numberOfAntiSlippingMattresses"
                      type="number"
                      min="0"
                      step="1"
                      value={trailer.numberOfAntiSlippingMattresses}
                      onChange={handleChange}
                      className="edit-trailer-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_sidebars",
                      {
                        defaultValue:
                          "N.º de Barras Laterais",
                      }
                    )}
                    icon={<FaBars />}
                    error={invalidFields.numberOfSidebars}
                    className="edit-trailer-span-3"
                  >
                    <input
                      name="numberOfSidebars"
                      type="number"
                      min="0"
                      step="1"
                      value={trailer.numberOfSidebars}
                      onChange={handleChange}
                      className="edit-trailer-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_fire_extinguishers",
                      {
                        defaultValue:
                          "N.º de Extintores",
                      }
                    )}
                    icon={<FaFireExtinguisher />}
                    error={invalidFields.numberOfFireExtinguishers}
                    className="edit-trailer-span-3"
                  >
                    <input
                      name="numberOfFireExtinguishers"
                      type="number"
                      min="0"
                      step="1"
                      value={trailer.numberOfFireExtinguishers}
                      onChange={handleChange}
                      className="edit-trailer-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>
                </div>
              </section>

              <section className="edit-trailer-sectionCard edit-trailer-documentsSection">
                <SectionHeading
                  number="3"
                  title={t(
                    "sections.documents_validities",
                    {
                      defaultValue:
                        "Documentos & Validades",
                    }
                  )}
                />

                <div className="edit-trailer-documentsGrid">
                  {documentItems.map((item) => (
                    <DocumentCard
                      key={item.key}
                      fieldName={item.key}
                      title={item.title}
                      file={item.file}
                      expiryField={item.expiryField}
                      expiryValue={item.expiryValue}
                      status={item.status}
                      fileError={invalidFields[item.key]}
                      expiryError={
                        invalidFields[item.expiryField]
                      }
                      onFileChange={handleFileChange}
                      onDateChange={handleChange}
                      onView={setViewerFile}
                      t={t}
                    />
                  ))}
                </div>
              </section>
            </form>
          </main>
        </div>
      </div>

      {successPopupOpen ? (
        <div
          className="edit-trailer-popupOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-trailer-success-title"
        >
          <div className="edit-trailer-popupModal">
            <div className="edit-trailer-popupIconWrap">
              <FaCheckCircle className="edit-trailer-popupIcon" />
            </div>

            <h2
              id="edit-trailer-success-title"
              className="edit-trailer-popupTitle"
            >
              {t(
                "popup.updated_title",
                {
                  defaultValue:
                    "Reboque atualizado",
                }
              )}
            </h2>

            <p className="edit-trailer-popupMessage">
              {t(
                "popup.updated_message",
                {
                  defaultValue:
                    "As alterações ao reboque foram guardadas com sucesso.",
                }
              )}
            </p>

            <button
              type="button"
              className="edit-trailer-popupPrimaryBtn"
              onClick={closeSuccessPopup}
            >
              {t(
                "popup.ok",
                {
                  defaultValue:
                    "OK",
                }
              )}
            </button>
          </div>
        </div>
      ) : null}

      {previewOpen ? (
        <div
          className="edit-trailer-previewOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-trailer-preview-title"
          onClick={() => {
            setPreviewOpen(false);
          }}
        >
          <div
            className="edit-trailer-previewModal"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <header className="edit-trailer-previewHeader">
              <div>
                <span>
                  {t(
                    "preview.eyebrow",
                    {
                      defaultValue:
                        "Pré-visualização",
                    }
                  )}
                </span>

                <h2 id="edit-trailer-preview-title">
                  {trailer.trailerPlate ||
                    t(
                      "preview.no_plate",
                      {
                        defaultValue:
                          "Reboque sem matrícula",
                      }
                    )}
                </h2>

                <p>
                  {brandTypeLabel}
                </p>
              </div>

              <button
                type="button"
                className="edit-trailer-previewClose"
                aria-label={t(
                  "buttons.close",
                  {
                    defaultValue:
                      "Fechar",
                  }
                )}
                onClick={() => {
                  setPreviewOpen(false);
                }}
              >
                ×
              </button>
            </header>

            <div className="edit-trailer-previewBody">
              <section className="edit-trailer-previewSection">
                <h3>
                  {t(
                    "sections.dimensions",
                    {
                      defaultValue:
                        "Dimensões",
                    }
                  )}
                </h3>

                <div className="edit-trailer-previewStats">
                  <div>
                    <span>
                      {t(
                        "labels.length_m",
                        {
                          defaultValue:
                            "Comprimento",
                        }
                      )}
                    </span>

                    <strong>
                      {trailer.length
                        ? `${trailer.length} m`
                        : "—"}
                    </strong>
                  </div>

                  <div>
                    <span>
                      {t(
                        "labels.width_m",
                        {
                          defaultValue:
                            "Largura",
                        }
                      )}
                    </span>

                    <strong>
                      {trailer.width
                        ? `${trailer.width} m`
                        : "—"}
                    </strong>
                  </div>

                  <div>
                    <span>
                      {t(
                        "labels.height_m",
                        {
                          defaultValue:
                            "Altura",
                        }
                      )}
                    </span>

                    <strong>
                      {trailer.height
                        ? `${trailer.height} m`
                        : "—"}
                    </strong>
                  </div>

                  <div>
                    <span>
                      {t(
                        "labels.weight_tons",
                        {
                          defaultValue:
                            "Peso",
                        }
                      )}
                    </span>

                    <strong>
                      {trailer.weight
                        ? `${trailer.weight} t`
                        : "—"}
                    </strong>
                  </div>
                </div>
              </section>

              <section className="edit-trailer-previewSection">
                <h3>
                  {t(
                    "sections.documents",
                    {
                      defaultValue:
                        "Documentos",
                    }
                  )}
                </h3>

                <div className="edit-trailer-previewDocuments">
                  {documentItems.map((item) => (
                    <div
                      key={item.key}
                      className="edit-trailer-previewDocument"
                    >
                      <span title={item.title}>
                        {item.title}
                      </span>

                      <DocumentStatusPill
                        status={item.status}
                      />

                      <strong>
                        {item.expiryValue ||
                          "—"}
                      </strong>
                    </div>
                  ))}
                </div>
              </section>

              <section className="edit-trailer-previewSection">
                <h3>
                  {t(
                    "sections.equipment",
                    {
                      defaultValue:
                        "Equipamento",
                    }
                  )}
                </h3>

                <div className="edit-trailer-previewEquipment">
                  {sidebarSpecifications
                    .slice(4)
                    .map((item) => (
                      <div key={item.label}>
                        <span>
                          {item.label}
                        </span>

                        <strong>
                          {item.value}
                        </strong>
                      </div>
                    ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      ) : null}

      {viewerData ? (
        <div
          className="edit-trailer-viewerOverlay"
          onClick={() => {
            setViewerFile(null);
          }}
        >
          <div
            className="edit-trailer-viewerModal"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <div className="edit-trailer-viewerHeader">
              <span
                className="edit-trailer-viewerTitle"
                title={viewerData.name}
              >
                {viewerData.name}
              </span>

              <div className="edit-trailer-viewerActions">
                <button
                  type="button"
                  className="edit-trailer-viewerActionBtn"
                  aria-label={t(
                    "buttons.download",
                    {
                      defaultValue:
                        "Descarregar",
                    }
                  )}
                  title={t(
                    "buttons.download",
                    {
                      defaultValue:
                        "Descarregar",
                    }
                  )}
                  onClick={handleDownloadViewerFile}
                >
                  <FaDownload />
                </button>

                <button
                  type="button"
                  className="edit-trailer-viewerClose"
                  onClick={() => {
                    setViewerFile(null);
                  }}
                  aria-label={t(
                    "buttons.close",
                    {
                      defaultValue:
                        "Fechar",
                    }
                  )}
                  title={t(
                    "buttons.close",
                    {
                      defaultValue:
                        "Fechar",
                    }
                  )}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="edit-trailer-viewerBody">
              {viewerData.isPdf ? (
                <iframe
                  title={
                    viewerData.name ||
                    "document"
                  }
                  src={viewerData.href}
                  className="edit-trailer-viewerFrame"
                />
              ) : viewerData.isImage ? (
                <img
                  src={viewerData.href}
                  alt={
                    viewerData.name ||
                    "document"
                  }
                  className="edit-trailer-viewerImage"
                />
              ) : (
                <div className="edit-trailer-viewerUnsupported">
                  <FaFileAlt />

                  <p>
                    {viewerData.name}
                  </p>

                  <button
                    type="button"
                    className="edit-trailer-viewerDownloadFallback"
                    onClick={handleDownloadViewerFile}
                  >
                    <FaDownload />

                    {t(
                      "buttons.download",
                      {
                        defaultValue:
                          "Descarregar",
                      }
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default EditTrailer;